import React, { useEffect, useLayoutEffect, useRef } from 'react';

declare const Quill: any;

interface RichEditorProps {
  value: string;
  onChange: (content: string) => void;
  placeholder?: string;
  height?: string | number;
  readOnly?: boolean;
}

const RichEditor: React.FC<RichEditorProps> = ({
  value,
  onChange,
  placeholder = '',
  height = '200px',
  readOnly = false,
}) => {
  const quillRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);

  useLayoutEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    if (containerRef.current && !quillRef.current) {
      const editorContainer = containerRef.current.appendChild(
        document.createElement('div')
      );

      const quill = new Quill(editorContainer, {
        theme: 'snow',
        placeholder,
        readOnly,
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            ['link', 'image'],
            ['clean'],
          ],
        },
        formats: [
          'header',
          'bold',
          'italic',
          'underline',
          'strike',
          'list',
          'bullet',
          'link',
          'image',
          'color',
          'background',
          'align',
        ],
      });

      quillRef.current = quill;

      if (value) {
        try {
          quill.root.innerHTML = value;
        } catch (error) {
          console.error('Error setting HTML content in Quill:', error);
          quill.setText(value || '');
        }
      }

      quill.on('text-change', () => {
        const html = quill.root.innerHTML;
        if (html !== value) {
          onChangeRef.current(html);
        }
      });
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
        quillRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.enable(!readOnly);
    }
  }, [readOnly]);

  useEffect(() => {
    if (quillRef.current && value !== quillRef.current.root.innerHTML) {
      quillRef.current.root.innerHTML = value || '';
    }
  }, [value]);

  return (
    <div
      ref={containerRef}
      style={{
        height,
        display: 'flex',
        flexDirection: 'column',
      }}
    />
  );
};

interface RichContentProps {
  value: string;
  fontSize?: string | number;
  fontFamily?: string;
  className?: string;
  style?: React.CSSProperties;
}

const RichContent: React.FC<RichContentProps> = ({
  value,
  fontSize,
  fontFamily,
  className,
  style,
}) => (
  <div className={`ql-snow ${className ?? ''}`} style={style}>
    <div
      className="ql-editor"
      style={{ padding: 0, overflow: 'visible', fontSize, fontFamily }}
      dangerouslySetInnerHTML={{ __html: value }}
    />
  </div>
);

export default RichEditor;
export { RichContent };
